import React, {useCallback, useEffect, useState} from "react";
import molEditorImage from "../../img/mol-editor.png";
import {DELETE_MARKER} from "../../lib/utils";
import {DropdownButton} from "react-bootstrap";
import _ from "lodash";
import CopyToClipboardButton from "../CopyToClipboardButton";
import DropdownFilter from "../DropdownFilter";
import PublicationTimeline from "../PublicationTimeline";
import {useInlineAnnotation} from "../InlineAnnotation";

const ResultsFilter = ({
  currentCategory,
  currentCategoryLabel,
  datasets = {},
  displayedItems = [],
  enableDSFilter = true,
  filteringDatasetKeys = null,
  filterString = "",
  getCategoryResultsClipboardText = () => "",
  includeQueryTerms = false,
  includeSalts = false,
  isAdmin = false,
  isDatasetsFilteringBusy = false,
  isFilterMatched = true,
  isFiltering = false,
  showResultsFilter = true,
  disabled = false,
  showLoading = false,
  displayedCount = null,
  onDatasetsFilterChange = () => {},
  onFilterStringChange = () => {},
  onIncludeQueryTermsChange = () => {},
  onIncludeSaltsChange = () => {},
  onTimelineItemClick = null,
  onTimelineReset = null,
  onComputeActivities = null,
  activityAvailable = false,
  activityComputing = false,
  activityProgress = null,
  onSubstructureFilter = null,
  onClearSubstructureFilter = null,
  substructureQuery = null,
  substructureLoading = false,
  searchButton = null,
  showIncludeQueryTermsToggler = false,
  showIncludeSaltsToggler = false,
  totalCount = null,
}) => {
  const allDatasetKeys = new Set(Object.keys(datasets));
  const selectedDatasetKeys = new Set(filteringDatasetKeys?.length ? filteringDatasetKeys : allDatasetKeys);
  const [inputValue, setInputValue] = useState(filterString);
  const { ref: activityRef, show: showActivityAnnotation, visible: activityAnnotationVisible,
          message: activityAnnotationMsg, type: activityAnnotationType } = useInlineAnnotation();

  const handleDatasetsFilterApply = (selected) => {
    onDatasetsFilterChange(selected.length === allDatasetKeys.size ? null : selected);
  };

  const handleDatasetsFilterClear = () => {
    onDatasetsFilterChange(null);
  }

  // Sync local input state with external filterString changes
  useEffect(() => {
    setInputValue(filterString);
  }, [filterString]);

  // Only call onFilterStringChange when inputValue changes, not when displayedCount changes
  // to avoid triggering unnecessary re-renders and state resets (PLEX-3304)
  useEffect(() => {
    onFilterStringChange(inputValue);
  }, [inputValue]);

  const reducedResults = isFiltering || !includeSalts;
  const verb = showLoading && !reducedResults ? "Loaded" : "Showing";
  const tally = `${displayedCount !== totalCount
                 ? displayedCount + " of"
                 : ""} ${totalCount !== null ? totalCount : '?'}`;
  const message = totalCount === null
    ? "Waiting for results..."
    : isDatasetsFilteringBusy
      ? "Filtering results by select datasets..."
      : totalCount === 0 && !showLoading
        ? "No Results"
        : `${verb} ${tally} ${currentCategoryLabel} results`;

  const isDSFilterActive = selectedDatasetKeys.size !== allDatasetKeys.size;
  const dsFilterMenuDisabled = disabled || isDatasetsFilteringBusy || !onDatasetsFilterChange || !enableDSFilter;
  const filterTitle = isDSFilterActive
                      ? `Filtering on ${Array.from(selectedDatasetKeys).map(x => datasets[x]?.name || x, []).join(", ")}`
                      : "Filter results by dataset"
  const waiting = "";
  const hasFilter = inputValue?.length;
  //const waiting = "Waiting for results...";

  const showTimeline = (currentCategory === "publication" || currentCategory === "patent") && displayedItems.length > 0;

  // Even when hiding the results filter, always create its container to preserve layout
  return (
    <div className={`grid-filter${!showResultsFilter ? " undecorated" : ""}`}
         style={!showResultsFilter ? {"visibility": "hidden"} : {}}>
      <span className="filter-summary">{!showResultsFilter ? waiting : message}</span>
      <div className="grid-filter-input">
        <input
          key={currentCategory}
          placeholder="filter results..."
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setInputValue("")}
          type="text"
          value={inputValue}
          disabled={disabled}
          className={isFilterMatched ? "" : "out-of-bounds"}
          title={isFilterMatched ? "" : "Nothing matched"}
        />
        <span
          className={`input-reset inside${hasFilter ? "" : " disabled"}${hasFilter && !isFilterMatched ? " out-of-bounds" : ""}`}
          title="Clear input"
          onClick={() => setInputValue("")}
          style={hasFilter && showResultsFilter ? {visibility: "visible"} : {}}>
          {DELETE_MARKER}
        </span>
        {currentCategory === "compound" && onComputeActivities && !activityAvailable && (
          <span ref={activityRef} className="inline-annotation-wrapper">
            <button
              className={`flat-button toggle-button compute-activity-button glyphicon glyphicon-screenshot${activityComputing ? " active" : ""}`}
              title={activityComputing ? "Computing target activity\u2026" : activityAvailable ? "Target activity computed" : "Compute target activity"}
              onClick={() => onComputeActivities(() => showActivityAnnotation("Done", "success"))}
              disabled={disabled || activityComputing || activityAvailable}
            >{activityComputing ? (() => {
              const CIRCUMFERENCE = 31.4;
              const phase = activityProgress?.phase;
              const isDone = phase === "done" && activityProgress.total > 0;
              const pct = isDone ? activityProgress.computed / activityProgress.total : 0.25;
              const offset = CIRCUMFERENCE * (1 - pct);
              return (
                <span className="activity-progress-ring">
                  <svg viewBox="0 0 14 14" className={isDone ? "" : "activity-progress-spinning"}>
                    <circle className="activity-progress-track" cx="7" cy="7" r="5"/>
                    <circle className="activity-progress-fill" cx="7" cy="7" r="5"
                      style={{strokeDashoffset: offset}}/>
                  </svg>
                </span>
              );
            })() : ""}</button>
            {activityAnnotationVisible && (
              <span className={`inline-annotation inline-annotation-${activityAnnotationType}`}>
                {activityAnnotationMsg}
              </span>
            )}
          </span>
        )}
        {currentCategory === "compound" && onSubstructureFilter && (
          <button
            className={`flat-button toggle-button substructure-filter-button${substructureQuery ? " active" : ""}`}
            title={substructureQuery
              ? `Substructure filter active: ${substructureQuery} (click to clear)`
              : "Filter by substructure"}
            onClick={() => substructureQuery ? onClearSubstructureFilter() : onSubstructureFilter()}
            disabled={disabled || substructureLoading}
          >
            {substructureLoading ? (
              <span className="activity-progress-ring">
                <svg viewBox="0 0 14 14" className="activity-progress-spinning">
                  <circle className="activity-progress-track" cx="7" cy="7" r="5"/>
                  <circle className="activity-progress-fill" cx="7" cy="7" r="5"/>
                </svg>
              </span>
            ) : (
              <img src={molEditorImage} className="substructure-icon" alt="substructure filter" />
            )}
          </button>
        )}
        <button
          style={showIncludeQueryTermsToggler && showResultsFilter ? {} : {display: "none"}}
          className={`toggle-button search-terms-display-toggle${includeQueryTerms ? "" : " active"} glyphicon glyphicon-eye-${includeQueryTerms ? "close" : "open"}`}
          title={(includeQueryTerms ? "Hide" : "Show") + " query items"}
          onClick={() => onIncludeQueryTermsChange(!includeQueryTerms)}
        />
        <button
          style={showIncludeSaltsToggler && showResultsFilter ? {} : {display: "none"}}
          className={`salts-display-toggle${includeSalts ? " active" : ""}`}
          title={(includeSalts ? "Showing" : "Hiding") + " salts in results"}
          onClick={() => onIncludeSaltsChange(!includeSalts)}
        >Na⁺</button>
        <DropdownButton
          title={<span title={filterTitle} className="glyphicon glyphicon-filter" />}
          className={`dataset-options${isDSFilterActive ? " active" : ""}`}
          disabled={dsFilterMenuDisabled}
          align="end"
        >
          <DropdownFilter
            items={Object.values(datasets)}
            currentFilterValue={Object.values(datasets).filter(x => selectedDatasetKeys.has(x.id))}
            onFilterApply={handleDatasetsFilterApply}
            onFilterClear={handleDatasetsFilterClear}
            getKey={x => x.id}
            getLabel={x => x.name}
            getTitle={x => x.description || x.name}
            disabled={dsFilterMenuDisabled}
          />
        </DropdownButton>
        {searchButton}
        <CopyToClipboardButton
          title="Copy this category results to clipboard"
          className={"flat-button"}
          disabled={disabled || showLoading || displayedCount === 0}
          getText={getCategoryResultsClipboardText}
        />
      </div>
      {showTimeline && (
        <PublicationTimeline
          items={displayedItems}
          category={currentCategory}
          onItemClick={onTimelineItemClick}
          onReset={onTimelineReset}
        />
      )}
    </div>
  );
};

export default ResultsFilter;
